<template>
  <div id="contenedor-obras-autorizadas">
    <v-container fluid>
      <v-card flat>
        <v-card-text>
          <!-- Título y Filtros -->
          <titulo-tool-bar titulo="Obras Autorizadas">
          </titulo-tool-bar>
          <!-- Componente de Filtros -->
          <filtros-obras-autorizadas
            v-model="filtros"
            @change="aplicarFiltros"
          />
        </v-card-text>
      </v-card>

      <!-- Resumen estadístico -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg" outlined>
            <v-card-text class="d-flex align-center">
              <v-avatar color="primary" size="48" class="mr-3">
                <v-icon dark>mdi-domain</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Total Obras</div>
                <div class="text-h5 font-weight-bold">
                  {{ paginacion.total }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg" outlined>
            <v-card-text class="d-flex align-center">
              <v-avatar color="success" size="48" class="mr-3">
                <v-icon dark>mdi-check-circle</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Total Autorizado</div>
                <div class="text-h6 font-weight-bold success--text">
                  {{ totales.totalAutorizadoGeneral | currency }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg" outlined>
            <v-card-text class="d-flex align-center">
              <v-avatar color="error" size="48" class="mr-3">
                <v-icon dark>mdi-close-circle</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Total Cancelado</div>
                <div class="text-h6 font-weight-bold error--text">
                  {{ totales.totalCanceladoGeneral | currency }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-lg" outlined>
            <v-card-text class="d-flex align-center">
              <v-avatar color="info" size="48" class="mr-3">
                <v-icon dark>mdi-currency-usd</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption grey--text">Balance Neto</div>
                <div class="text-h6 font-weight-bold">
                  {{ (totales.totalAutorizadoGeneral - totales.totalCanceladoGeneral) | currency }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabla de Obras con Oficios -->
      <v-card class="rounded-lg">
        <v-data-table
          :headers="headers"
          :items="getFilasExpandidas"
          :items-per-page="-1"
          :loading="$apollo.loading"
          hide-default-footer
          class="obras-table"
        >
            <!-- Clave de Obra -->
            <template v-slot:item.clave="{ item }">
              <div v-if="item.tipo === 'obra'" class="font-weight-bold primary--text">
                {{ item.clave }}
              </div>
              <div v-else class="pl-8">
                <v-chip
                  x-small
                  :color="getTipoColor(item.tipoOficio)"
                  dark
                >
                  <v-icon x-small left>mdi-label</v-icon>
                  {{ item.tipoOficio }}
                </v-chip>
              </div>
            </template>

            <!-- Meta / Número Oficio -->
            <template v-slot:item.meta="{ item }">
              <div v-if="item.tipo === 'obra'" class="text-caption">
                <cortar-textos :texto="item.meta" :cantidad-caracteres="80" />
              </div>
              <div v-else class="pl-8">
                <div class="font-weight-medium">
                  <v-icon x-small class="mr-1">mdi-file-document</v-icon>
                  {{ item.numeroOficio }}
                </div>
                <div class="text-caption grey--text mt-1">
                  <v-icon x-small class="mr-1">mdi-bank</v-icon>
                  {{ item.fuenteFinanciera }}
                </div>
              </div>
            </template>

            <!-- Fecha Oficio -->
            <template v-slot:item.ejercicio="{ item }">
              <div v-if="item.tipo === 'obra'">
                -
              </div>
              <div v-else class="pl-8">
                <v-chip x-small outlined>
                  <v-icon x-small left>mdi-calendar</v-icon>
                  {{ item.fechaOficio | formatoFecha }}
                </v-chip>
              </div>
            </template>

            <!-- Total Autorizado / Importe Obra -->
            <template v-slot:item.totalAutorizado="{ item }">
              <div v-if="item.tipo === 'obra'" class="text-right">
                <v-chip small color="success" dark>
                  <v-icon small left>mdi-check-circle</v-icon>
                  {{ item.totalAutorizado | currency }}
                </v-chip>
              </div>
              <div v-else class="text-right pl-8">
                <span class="font-weight-bold primary--text">
                  {{ item.importeObra | currency }}
                </span>
              </div>
            </template>

            <!-- Total Cancelado / Importe Oficio -->
            <template v-slot:item.totalCancelado="{ item }">
              <div
                v-if="item.tipo === 'obra'"
                class="text-right">
                <v-chip
                  small
                  dark
                  :color="item.totalCancelado > 0 ? 'error' : 'grey darken-1'"
                >
                  <v-icon small left>
                    {{ item.totalCancelado > 0 ? 'mdi-close-circle' : 'mdi-minus-circle' }}
                  </v-icon>
                  {{ item.totalCancelado | currency }}
                </v-chip>
              </div>
              <div v-else-if="item.tipoOficio !== 'AUTORIZACIÓN'"
                class="text-right pl-8 grey--text text--darken-2">
                {{ item.importeObra | currency }}
              </div>
            </template>

            <!-- PDF del Oficio -->
            <template v-slot:item.pdf="{ item }">
              <div v-if="item.tipo === 'obra'" class="text-center">
                -
              </div>
              <div v-else class="text-center">
                <v-tooltip v-if="item.fileData" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop="verPdf(item)">
                      <v-icon small>mdi-file-pdf-box</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver PDF del Oficio</span>
                </v-tooltip>
                <span v-else class="grey--text text-caption">—</span>
              </div>
            </template>

            <!-- Empty state -->
            <template v-slot:no-data>
              <div class="text-center py-8">
                <v-icon size="64" color="grey lighten-1">
                  mdi-domain
                </v-icon>
                <div class="text-h6 grey--text mt-4">
                  No hay obras autorizadas
                </div>
                <div class="text-body-2 grey--text">
                  Las obras con oficios de autorización aparecerán aquí
                </div>
              </div>
            </template>
        </v-data-table>
      </v-card>

      <!-- Paginación -->
      <v-card class="mt-4 rounded-lg" outlined>
        <v-card-text class="d-flex justify-center align-center flex-wrap">
          <v-pagination
            v-model="filtros.pagina"
            :length="paginacion.totalPaginas"
            :total-visible="7"
            circle
            color="primary"
            @input="buscar"
          ></v-pagination>
          <div class="ml-4 text-caption grey--text">
            Página {{ paginacion.pagina }} de {{ paginacion.totalPaginas }}
            ({{ paginacion.total }} obras)
          </div>
        </v-card-text>
      </v-card>

      <!-- Visor de PDF -->
      <v-dialog
        v-if="mostrarVerPdf"
        v-model="mostrarVerPdf"
        persistent
        scrollable
        width="750px">
        <v-card class="rounded-lg">
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-file-pdf-box</v-icon>
            {{ oficioSeleccionado.numeroOficio }}
            <v-spacer></v-spacer>
            <v-btn icon @click="mostrarVerPdf = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <ver-pdf
              :rutaArchivo="oficioSeleccionado.fileData"
              :nombreArchivoDescarga="nombreArchivoDescarga" />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import OBRAS_SOLICITUDES_PRESUPUESTO from '@/graphql/querys/administracion/obrassolicitudes/obrasSolicitudesPresupuesto.gql';
import TituloToolBar from '@/components/TituloToolBar.vue';
import CortarTextos from '@/components/CortarTextos.vue';
import FiltrosObrasAutorizadas from '@/components/administracion/obrassolicitudes/FiltrosObrasAutorizadas.vue';
import VerPdf from '@/components/administracion/auditorias/auditoria/verPdf.vue';

export default {
  name: 'ObrasAutorizadas',
  components: {
    TituloToolBar,
    CortarTextos,
    FiltrosObrasAutorizadas,
    VerPdf,
  },
  data() {
    return {
      filtros: {
        ejercicio: new Date().getFullYear(),
        buscarTexto: '',
        idFuenteFinanciera: null,
        pagina: 1,
        limite: 10,
        ordenarPor: 'createdAt',
        orden: 'DESC',
      },
      obrasSolicitudesPresupuesto: null,
      mostrarVerPdf: false,
      oficioSeleccionado: {},
      nombreArchivoDescarga: null,
      headers: [
        {
          text: 'Clave Obra',
          value: 'clave',
          sortable: false,
          align: 'start',
        },
        {
          text: 'Meta / Oficio',
          value: 'meta',
          sortable: false,
          align: 'start',
        },
        {
          text: 'Fecha',
          value: 'ejercicio',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Autorizado',
          value: 'totalAutorizado',
          sortable: false,
          align: 'end',
        },
        {
          text: 'Cancelado',
          value: 'totalCancelado',
          sortable: false,
          align: 'end',
        },
        {
          text: 'PDF',
          value: 'pdf',
          sortable: false,
          align: 'center',
          width: '80px',
        },
      ],
    };
  },
  apollo: {
    obrasSolicitudesPresupuesto: {
      query: OBRAS_SOLICITUDES_PRESUPUESTO,
      variables() {
        return {
          ejercicio: this.filtros.ejercicio,
          buscarTexto: this.filtros.buscarTexto || null,
          idFuenteFinanciera: this.filtros.idFuenteFinanciera || null,
          pagina: this.filtros.pagina,
          limite: this.filtros.limite,
          ordenarPor: this.filtros.ordenarPor,
          orden: this.filtros.orden,
        };
      },
      skip() {
        return !this.filtros.ejercicio;
      },
    },
  },
  computed: {
    getListadoObrasSolicitadas() {
      if (!this.obrasSolicitudesPresupuesto) return [];
      if (!this.obrasSolicitudesPresupuesto.datos) return [];

      return this.obrasSolicitudesPresupuesto.datos;
    },
    getFilasExpandidas() {
      const filas = [];

      this.getListadoObrasSolicitadas.forEach((obraSolicitud) => {
        // Agregar fila de obra
        filas.push({
          id: `obra-${obraSolicitud.id}`,
          tipo: 'obra',
          clave: obraSolicitud.obra.clave,
          meta: obraSolicitud.obra.meta,
          ejercicio: obraSolicitud.obra.ejercicio,
          totalAutorizado: obraSolicitud.totalAutorizado,
          totalCancelado: obraSolicitud.totalCancelado,
        });

        // Agregar filas de autorizaciones
        if (obraSolicitud.autorizaciones && obraSolicitud.autorizaciones.length > 0) {
          obraSolicitud.autorizaciones.forEach((autorizacion) => {
            filas.push({
              id: `autorizacion-${autorizacion.id}`,
              tipo: 'autorizacion',
              obraId: obraSolicitud.id,
              tipoOficio: autorizacion.oficioAutorizacion.tipo,
              numeroOficio: autorizacion.oficioAutorizacion.numero,
              fechaOficio: autorizacion.oficioAutorizacion.fecha,
              fuenteFinanciera: autorizacion.fuenteFinanciera?.nombre || 'Sin fuente',
              importeObra: autorizacion.importe,
              importeOficio: autorizacion.oficioAutorizacion.importe,
              clavePresupuestal: autorizacion.clavePresupuestal,
              fileData: autorizacion.oficioAutorizacion.fileData,
            });
          });
        }
      });

      return filas;
    },
    paginacion() {
      if (!this.obrasSolicitudesPresupuesto) {
        return {
          total: 0,
          pagina: 1,
          limite: 10,
          totalPaginas: 1,
        };
      }

      return this.obrasSolicitudesPresupuesto.paginacion || {
        total: 0,
        pagina: 1,
        limite: 10,
        totalPaginas: 1,
      };
    },
    totales() {
      if (!this.obrasSolicitudesPresupuesto) {
        return {
          totalAutorizadoGeneral: 0,
          totalCanceladoGeneral: 0,
        };
      }

      return this.obrasSolicitudesPresupuesto.totales || {
        totalAutorizadoGeneral: 0,
        totalCanceladoGeneral: 0,
      };
    },
  },
  methods: {
    aplicarFiltros() {
      // Resetear a la primera página cuando se cambian los filtros
      this.filtros.pagina = 1;
      this.buscar();
    },
    buscar() {
      this.$apollo.queries.obrasSolicitudesPresupuesto.refetch();
    },
    getTipoColor(tipo) {
      const colores = {
        A: 'blue',
        B: 'green',
        C: 'orange',
        D: 'purple',
      };
      return colores[tipo] || 'grey';
    },
    verPdf(item) {
      this.oficioSeleccionado = item;
      this.nombreArchivoDescarga = `Oficio-${item.numeroOficio}`;
      this.mostrarVerPdf = true;
    },
  },
  filters: {
    formatoFecha(fecha) {
      if (!fecha) return '-';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.obras-table {
  ::v-deep tbody tr {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(var(--v-primary-base), 0.04) !important;
    }
  }
}

.v-card__title {
  word-break: break-word;
}
</style>
